import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Headphones, Plus, MessageCircle, Monitor, Wrench, Package,
  Users, Clock, CheckCircle2, AlertCircle, Send, Copy, RefreshCw,
  ExternalLink, Shield, Loader2
} from 'lucide-react';

const CATEGORIES = [
  { id: 'technical', label: 'Assistenza Tecnica', icon: Wrench, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  { id: 'product_info', label: 'Informazioni Prodotti', icon: Package, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  { id: 'customer_care', label: 'Customer Care', icon: Users, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
];

const STATUS_MAP = {
  open: { label: 'Aperto', color: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20', icon: AlertCircle },
  in_progress: { label: 'In Lavorazione', color: 'bg-blue-500/10 text-blue-700 border-blue-500/20', icon: Clock },
  resolved: { label: 'Risolto', color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20', icon: CheckCircle2 },
};

const SupportPage = () => {
  const { API, getAuthHeaders } = useAuth();
  const { branding } = useBranding();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNewTicketOpen, setIsNewTicketOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [remoteSession, setRemoteSession] = useState(null);
  const [generatingSession, setGeneratingSession] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [newTicket, setNewTicket] = useState({ category: '', subject: '', message: '', priority: 'normal' });

  const supportWhatsapp = branding?.support_whatsapp || '';

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`, getAuthHeaders());
      setTickets(res.data);
    } catch (e) {
      console.error('Error fetching tickets:', e);
    } finally {
      setLoading(false);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { fetchTickets(); }, []);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/support/tickets`, newTicket, getAuthHeaders());
      setNewTicket({ category: '', subject: '', message: '', priority: 'normal' });
      setIsNewTicketOpen(false);
      fetchTickets();
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore nella creazione del ticket');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    try {
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, { message: replyText }, getAuthHeaders());
      setReplyText('');
      // Refresh ticket
      const res = await axios.get(`${API}/support/tickets/${selectedTicket.id}`, getAuthHeaders());
      setSelectedTicket(res.data);
      fetchTickets();
    } catch (e) {
      alert('Errore nell\'invio del messaggio');
    }
  };

  const generateRemoteSession = async () => {
    setGeneratingSession(true);
    try {
      const res = await axios.post(`${API}/support/remote-session`, {}, getAuthHeaders());
      setRemoteSession(res.data);
    } catch (e) {
      alert('Errore nella generazione della sessione');
    } finally {
      setGeneratingSession(false);
    }
  };

  const openWhatsApp = () => {
    const phone = supportWhatsapp.replace(/[^0-9+]/g, '');
    window.open(`https://wa.me/${phone.replace('+', '')}`, '_blank');
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-outfit">Assistenza</h1>
          <p className="text-muted-foreground mt-1">Supporto tecnico, informazioni e assistenza remota</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {supportWhatsapp && (
            <Button variant="outline" className="gap-2 text-green-600 border-green-300 hover:bg-green-50 w-full sm:w-auto" onClick={openWhatsApp} data-testid="whatsapp-support-btn">
              <MessageCircle className="w-4 h-4" />
              Assistenza WhatsApp
            </Button>
          )}
          <Dialog open={isNewTicketOpen} onOpenChange={setIsNewTicketOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-ticket-btn" className="w-full sm:w-auto"><Plus className="w-4 h-4 mr-2" />Nuovo Ticket</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-outfit">Apri Nuovo Ticket</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTicket} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Categoria *</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setNewTicket(p => ({ ...p, category: cat.id }))}
                        className={`p-3 rounded-lg border text-center transition-all ${newTicket.category === cat.id ? 'ring-2 ring-primary border-primary bg-primary/5' : 'hover:border-muted-foreground/30'}`}
                        data-testid={`cat-${cat.id}`}
                      >
                        <cat.icon className={`w-5 h-5 mx-auto mb-1 ${cat.color}`} />
                        <p className="text-xs font-medium">{cat.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Oggetto *</Label>
                  <Input value={newTicket.subject} onChange={e => setNewTicket(p => ({ ...p, subject: e.target.value }))} placeholder="Breve descrizione del problema" required data-testid="ticket-subject" />
                </div>
                <div className="space-y-2">
                  <Label>Messaggio *</Label>
                  <Textarea value={newTicket.message} onChange={e => setNewTicket(p => ({ ...p, message: e.target.value }))} placeholder="Descrivi in dettaglio il tuo problema o richiesta..." rows={4} required data-testid="ticket-message" />
                </div>
                <div className="space-y-2">
                  <Label>Priorità</Label>
                  <Select value={newTicket.priority} onValueChange={v => setNewTicket(p => ({ ...p, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Bassa</SelectItem>
                      <SelectItem value="normal">Normale</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsNewTicketOpen(false)}>Annulla</Button>
                  <Button type="submit" disabled={!newTicket.category || !newTicket.subject || !newTicket.message} data-testid="submit-ticket-btn">Invia Ticket</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="tickets">
        <TabsList>
          <TabsTrigger value="tickets" className="gap-1.5"><Headphones className="w-4 h-4" />Ticket</TabsTrigger>
          <TabsTrigger value="remote" className="gap-1.5"><Monitor className="w-4 h-4" />Assistenza Remota</TabsTrigger>
        </TabsList>

        {/* ─── TICKETS TAB ─── */}
        <TabsContent value="tickets" className="mt-4 space-y-4">
          {selectedTicket ? (
            /* Ticket Detail */
            <Card data-testid="ticket-detail">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(null)} className="text-xs">&larr; Torna alla lista</Button>
                  <Badge variant="outline" className={STATUS_MAP[selectedTicket.status]?.color}>
                    {STATUS_MAP[selectedTicket.status]?.label}
                  </Badge>
                </div>
                <CardTitle className="font-outfit mt-2">{selectedTicket.subject}</CardTitle>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="secondary" className="text-xs">{CATEGORIES.find(c => c.id === selectedTicket.category)?.label}</Badge>
                  <span>{new Date(selectedTicket.created_at).toLocaleString('it-IT')}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ScrollArea className="max-h-[350px]">
                  <div className="space-y-3">
                    {(selectedTicket.messages || []).map(msg => (
                      <div key={msg.id} className={`p-3 rounded-lg ${msg.sender === 'user' ? 'bg-primary/5 ml-6' : 'bg-muted mr-6'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium">{msg.sender === 'user' ? 'Tu' : 'Supporto'}</span>
                          <span className="text-[10px] text-muted-foreground">{new Date(msg.created_at).toLocaleString('it-IT')}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                {selectedTicket.status !== 'resolved' && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Scrivi un messaggio..." onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleReply()} data-testid="ticket-reply-input" />
                    <Button onClick={handleReply} disabled={!replyText.trim()} data-testid="ticket-reply-btn"><Send className="w-4 h-4" /></Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            /* Ticket List */
            tickets.length > 0 ? (
              <div className="space-y-3">
                {tickets.map(ticket => {
                  const cat = CATEGORIES.find(c => c.id === ticket.category);
                  const status = STATUS_MAP[ticket.status];
                  const CatIcon = cat?.icon || Headphones;
                  return (
                    <Card key={ticket.id} className="cursor-pointer hover:border-primary/30 transition-all" onClick={() => setSelectedTicket(ticket)} data-testid={`ticket-${ticket.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg ${cat?.bg} flex items-center justify-center flex-shrink-0`}>
                            <CatIcon className={`w-5 h-5 ${cat?.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-sm truncate">{ticket.subject}</h3>
                              <Badge variant="outline" className={`text-[10px] ${status?.color}`}>{status?.label}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{ticket.message}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">{new Date(ticket.created_at).toLocaleString('it-IT')}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Headphones className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">Nessun ticket</h3>
                  <p className="text-muted-foreground text-sm max-w-md mx-auto">
                    Non hai ancora aperto nessun ticket. Crea un nuovo ticket se hai bisogno di assistenza.
                  </p>
                </CardContent>
              </Card>
            )
          )}
        </TabsContent>

        {/* ─── REMOTE TAB ─── */}
        <TabsContent value="remote" className="mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Remote Session Card */}
            <Card>
              <CardHeader>
                <CardTitle className="font-outfit flex items-center gap-2 text-lg">
                  <Monitor className="w-5 h-5" />Assistenza Remota
                </CardTitle>
                <CardDescription>
                  Genera un codice di sessione per permettere a un tecnico di assisterti in remoto
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {remoteSession ? (
                  <div className="space-y-4">
                    <div className="p-6 rounded-xl bg-muted/50 border text-center space-y-3" data-testid="remote-session-active">
                      <Shield className="w-8 h-8 text-primary mx-auto" />
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Il tuo codice sessione</p>
                      <p className="text-4xl font-bold font-mono tracking-widest" data-testid="session-code">{remoteSession.code}</p>
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { navigator.clipboard.writeText(remoteSession.code); alert('Codice copiato!'); }}>
                        <Copy className="w-3.5 h-3.5" />Copia Codice
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3.5 h-3.5" />
                      Scade: {new Date(remoteSession.expires_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <Button variant="outline" className="w-full" onClick={generateRemoteSession}>
                      <RefreshCw className="w-4 h-4 mr-2" />Genera Nuovo Codice
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Button onClick={generateRemoteSession} disabled={generatingSession} className="gap-2" data-testid="generate-session-btn">
                      {generatingSession ? <Loader2 className="w-4 h-4 animate-spin" /> : <Monitor className="w-4 h-4" />}
                      Avvia Sessione Remota
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card>
              <CardHeader>
                <CardTitle className="font-outfit text-lg">Come Funziona</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">1</div>
                    <div>
                      <p className="text-sm font-medium">Genera il codice</p>
                      <p className="text-xs text-muted-foreground">Clicca "Avvia Sessione Remota" per ottenere un codice univoco.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">2</div>
                    <div>
                      <p className="text-sm font-medium">Comunica il codice</p>
                      <p className="text-xs text-muted-foreground">Fornisci il codice all'operatore del supporto tecnico tramite chat o telefono.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">3</div>
                    <div>
                      <p className="text-sm font-medium">Connessione sicura</p>
                      <p className="text-xs text-muted-foreground">Il tecnico si collegherà in modo sicuro per assisterti. Il codice scade dopo 30 minuti.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupportPage;
